import type { MindItemSearchResult } from '@kimuno/api-sdk/lib/types/responses/MindItem';
import { Box } from '@mui/material';
import React, { useContext } from 'react';
import { Tab } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroller';
import { useSelector } from 'react-redux';

import {
	getPaginationSelector,
	getSearchResults as getSearchResultsSelector,
	getVideoMindItemsLoadingState as getVideoMindItemsLoadingStateSelector,
	SearchLoadingStatus,
} from '../../../../reducers/search.selectors';
import { trackingEvents } from '../../../../services/tracking/trackConfig';
import { MasterLayoutContext } from '../../../../state/MasterLayoutProvider';
import { SearchResultsTab } from '../../../../types/search';
import { hasNext } from '../../../../utils/pagination';
import useSearchExperiments from '../../../../utils/useSearchExperiments';
import VideoMuiCard, {
	type VideoMuiCardProps,
} from '../../../cards/MuiCard/VideoMuiCard/VideoMuiCard';
import WithTracking from '../../../utils/WithTracking';
import RelatedTopicsPills, {
	RELATED_TOPICS_INLINE_INDEX,
} from '../../RelatedTopicsPills/RelatedTopicsPills';
import EmptySearchResultsNewUI from '../EmptySearchResultsNewUI';
import { getCommonCardProps } from '../SearchResults';
import SearchResultsContainer from '../SearchResultsContainer';
import SearchResultsLoader from '../SearchResultsLoader';
import SearchResultsTopbarNewUI from '../SearchResultsTopbar';
import SingleSearchResultContainer from '../SingleSearchResultContainer';
import type { SearchResultsTabProps } from './SearchResultsTab';

export const getVideoMuiCardProps = (
	item: MindItemSearchResult,
	useAiFilters?: boolean
): VideoMuiCardProps => {
	return {
		...getCommonCardProps(item, useAiFilters),
		product: item.populatedProduct,
	};
};

const VideoMindItemsTab = ({
	searchTerm,
	requestFilters,
	loadMore,
	searchEngineVersion,
	onShowNotification,
	onRelatedTopicSelect,
}: SearchResultsTabProps) => {
	const videoMindItems = useSelector((state) => getSearchResultsSelector(state, 'videoMindItems'));
	const videoMindItemsPagination = useSelector(getPaginationSelector('videoMindItems'));
	const videoMindItemsLoadingState = useSelector(getVideoMindItemsLoadingStateSelector);
	const { mainContentContainer } = useContext(MasterLayoutContext);
	const { useAiFilters } = useSearchExperiments();

	return (
		<Tab.Pane eventKey={SearchResultsTab.VIDEOS} data-testid="new-ui-video-tab">
			<SearchResultsTopbarNewUI
				total={videoMindItemsPagination.total}
				searchTerm={searchTerm}
				isLoaded={videoMindItemsLoadingState === SearchLoadingStatus.LOADED}
				requestFilters={requestFilters}
			/>
			{videoMindItemsLoadingState === SearchLoadingStatus.INITIAL ||
			videoMindItemsLoadingState === SearchLoadingStatus.NONE ? (
				<SearchResultsLoader />
			) : (
				<>
					{videoMindItems.length > 0 ? (
						<InfiniteScroll
							loadMore={loadMore}
							hasMore={
								hasNext(videoMindItemsPagination) &&
								videoMindItemsLoadingState !== SearchLoadingStatus.PAGINATION
							}
							className="search-results__infinite-scroll-box"
							useWindow={false}
							getScrollParent={() => mainContentContainer}
						>
							<SearchResultsContainer>
								{videoMindItems.map((videoMindItem, index) => (
									<React.Fragment key={videoMindItem.id}>
										<SingleSearchResultContainer>
											<WithTracking
												events={{
													onClick: trackingEvents.itemOpen,
													onMiddleClick: trackingEvents.itemOpen,
													onContextMenu: trackingEvents.itemOpen,
												}}
												eventPayload={{
													other: {
														minditem_id: videoMindItem.id,
														index,
														searchEngineVersion,
													},
												}}
											>
												<Box>
													<VideoMuiCard
														{...getVideoMuiCardProps(videoMindItem, useAiFilters)}
														index={index}
														onShowNotification={onShowNotification}
													/>
												</Box>
											</WithTracking>
										</SingleSearchResultContainer>
										{index === RELATED_TOPICS_INLINE_INDEX && onRelatedTopicSelect && (
											<RelatedTopicsPills variant="inline" term={searchTerm} onSelect={onRelatedTopicSelect} />
										)}
									</React.Fragment>
								))}
							</SearchResultsContainer>
						</InfiniteScroll>
					) : (
						<SearchResultsContainer>
							<EmptySearchResultsNewUI searchTerm={searchTerm} />
						</SearchResultsContainer>
					)}
					{videoMindItemsLoadingState === SearchLoadingStatus.PAGINATION && <SearchResultsLoader />}
				</>
			)}
		</Tab.Pane>
	);
};

export default VideoMindItemsTab;
